import { logSpecificPageView } from 'mParticle/mParticleService'
import Head from 'next/head'
import { useEffect } from 'react'
import { SearchFiltersRefinement, SortOption, getSortOptionByAlgoliaIndex } from 'search'

import Bugsnag, { NotifiableError } from '@bugsnag/js'
import { isAxiosError } from 'axios'
import { EXPERIMENT_NAME } from 'experiments/constants/Experiments'
import { canonicalSlugMap } from 'head/utils/seoHelpers'
import { toProductDataItem } from 'layout/navigation/search/utils'
import { serverSideConfigs, t } from 'localization'
import merge from 'lodash/merge'
import { useRouter } from 'next/router'
import ErrorPage from 'pages/_error'
import { serverAssign } from 'server/services/serverExperimentService'
import { serverRequestService } from 'server/services/serverRequestService'
import { getConsumerSearchServerQueries } from 'server/utils/consumerSearchUtils'
import { logger } from 'server/utils/logger'
import Grid, { GridConfig } from 'shared/components/Grid'
import PageContextProvider from 'shared/contexts/PageContextProvider'
import { FeatureFlag } from 'shared/enums/FeatureFlag'
import SpecificPageTrackingEvents from 'shared/enums/SpecificPageTrackingEvents'
import { ProductSearchResponse } from 'shared/types/ConsumerSearch'
import { SearchState } from 'shared/types/Search'
import { FCGetServerSidePropsContext, getServerPageProps } from 'shared/utils/getServerSideProps'
import { transformAggregationsToStructuredData } from 'shared/utils/search/cs-utils'
import { urlToSearchState } from 'shared/utils/search/search-url-utils'
import { trackSnapchatPageView } from 'tracking/snapchatService'

const TP = 'pages.collections.slug'

type Collection = {
  cleanPictureUrl: string
  description: string
  id: number
  indexName?: string
  isUsedCollection: boolean
  metadataDescription: string
  name: string
  ordering: number
  slug: string
  sortedReplicaQuery?: { indexName: string }
  status: string
  subDescription: string
  title: string
  type: string
}

type SuccessProps = {
  baseCSSearchState: SearchState
  collection: Collection
  initialSearchState: SearchState
  slug: string
  /**
   * SSR Darwin Group.
   * It's used to determine the place for the size filters.
   * TODO: remove once Size Filter Placement Experiment is finished
   */
  ssrDarwinGroup?: string
}

type ConsumerSearchProps = {
  consumerSearchResult?: ProductSearchResponse
  consumerSearchTrendingResults?: ProductSearchResponse
}

type ErrorProps = {
  error: unknown
  errorStatus: number
}

type CollectionPageProps = (SuccessProps & ConsumerSearchProps) | (ErrorProps & ConsumerSearchProps)

const isErrorProps = (props: CollectionPageProps): props is ErrorProps => {
  return (props as ErrorProps).error !== undefined
}

const CollectionPage = (props: CollectionPageProps) => {
  if (isErrorProps(props)) {
    const { error, errorStatus } = props
    if (error) {
      return <ErrorPage statusCode={errorStatus} />
    }
    return null
  }

  const {
    baseCSSearchState,
    collection,
    consumerSearchResult,
    consumerSearchTrendingResults,
    initialSearchState,
    slug,
    ssrDarwinGroup,
  } = props

  const sortOption =
    getSortOptionByAlgoliaIndex(collection?.indexName ?? '') || SortOption.Collection
  const router = useRouter()

  useEffect(() => {
    logSpecificPageView({
      slug,
      pageName: SpecificPageTrackingEvents.CollectionPage,
      slugType: 'collection',
    })
    trackSnapchatPageView()
  }, [])

  const hiddenFilters: Set<string> = new Set()
  // currently presentation sizes are only used for apparel
  hiddenFilters.add(SearchFiltersRefinement.PresentationSizes)
  const config: GridConfig = {
    context: {
      hiddenFilters,
      hasNewProductOnly: false,
      filters: `collection_slugs:${slug}`,
      sortOption,
      ruleContexts: [`collection-${slug}`],
    },
    /**
     * regex looks for /collections and grabs everything after collections
     */
    regex: new RegExp(/(\/collections)(.*)/g),
    indexPath: '/collections',
  }

  return (
    <>
      <PageContextProvider value={SpecificPageTrackingEvents.CollectionPage}>
        <Head>
          <title>
            {t(`${TP}.pageTitle`, {
              defaultValue: '{title} | Flight Club',
              title: collection?.title,
            })}
          </title>
          {canonicalSlugMap.has(slug) && (
            <link
              key="canonical"
              rel="canonical"
              href={`https://www.flightclub.com${canonicalSlugMap.get(slug)}${
                router?.query?.page && `?page=${router?.query?.page}`
              }`}
            />
          )}
        </Head>
        <Grid
          baseCSSearchState={baseCSSearchState}
          config={config}
          consumerSearchResult={consumerSearchResult}
          consumerSearchState={initialSearchState}
          consumerSearchTrendingResults={consumerSearchTrendingResults}
          description={collection?.description}
          title={collection?.title}
          ssrDarwinGroup={ssrDarwinGroup}
        />
      </PageContextProvider>
    </>
  )
}

export const getServerSideProps = async (context: FCGetServerSidePropsContext) => {
  const { req, res, query: qsSNext, resolvedUrl, locale } = context
  const { featureFlags } = req
  const slugPath = Array.isArray(qsSNext.slug) ? qsSNext.slug.join('/') : qsSNext.slug

  try {
    // SSR EXPERIMENT web_fc_size_filter_placement_v2 INIT
    const areExperimentsEnabled = featureFlags?.[FeatureFlag.WEB_FC_ENABLE_EXPERIMENTS] ?? false
    const isSizeFilterPlacementExperimentEnabled =
      featureFlags?.[FeatureFlag.TEMP_WEB_FC_ENABLE_SIZE_FILTER_PLACEMENT_EXPERIMENT] ?? false
    const darwinGroup = await (async () => {
      if (areExperimentsEnabled && isSizeFilterPlacementExperimentEnabled) {
        const experimentName: EXPERIMENT_NAME = 'web_fc_size_filter_placement_v2'
        try {
          const assignResponse = await serverAssign({ experimentName, req, res })
          return assignResponse?.group?.name ?? 'control'
        } catch (error) {
          return 'control'
        }
      }

      return 'control'
    })()
    // SSR EXPERIMENT web_fc_size_filter_placement_v2 END

    const { data: collection } = await serverRequestService(req, res, 'sneakers').get<Collection>(
      `api/v1/collections/${qsSNext.slug}?excludeProductTemplates=true`,
    )

    const sortOption =
      getSortOptionByAlgoliaIndex(collection?.indexName ?? '') || SortOption.Collection
    const baseCSSearchState: SearchState = {
      refinementList: {
        collection_slugs: [slugPath],
      },
      sortOption: sortOption,
    }

    const queryParamSearchState: SearchState = urlToSearchState(qsSNext ? qsSNext : {})
    // here get the filters from the refinement list, build the query and then make the call
    const consumerSearchState = merge({}, baseCSSearchState, queryParamSearchState, {
      collection: slugPath,
    })

    const [consumerSearchResults, allFiltersSearchResults, trendingSearchResults] =
      await Promise.allSettled(
        getConsumerSearchServerQueries({
          baseSearchState: baseCSSearchState,
          consumerSearchState,
          req,
        }),
      )
    const { error: consumerSearchError, data: consumerSearchData } =
      consumerSearchResults.status === 'fulfilled' ? consumerSearchResults.value : {}

    const { data: allFiltersData } =
      allFiltersSearchResults.status === 'fulfilled' ? allFiltersSearchResults.value : {}

    const { error: trendingError, data: trendingData } =
      trendingSearchResults.status === 'fulfilled' ? trendingSearchResults.value : {}

    const consumerSearchResult = {
      aggregation: {
        filters: transformAggregationsToStructuredData(
          consumerSearchData?.aggregationValuesList || [],
        ),
      },
      allFilters: transformAggregationsToStructuredData(
        allFiltersData?.aggregationValuesList || [],
      ),
      data: toProductDataItem(consumerSearchData?.productsList || []),
      error: consumerSearchError || null,
      total: consumerSearchData?.totalResults || 0,
    }
    const consumerSearchTrendingResults = {
      data: toProductDataItem(trendingData?.productsList || []),
      error: trendingError || null,
      total: trendingData?.totalResults || 0,
    }

    return {
      props: {
        ...(await getServerPageProps(context)),
        ...JSON.parse(
          JSON.stringify({
            ...(await serverSideConfigs(res, locale)),
            baseCSSearchState,
            initialSearchState: consumerSearchState,
            slug: slugPath,
            collection,
            consumerSearchResult,
            consumerSearchTrendingResults,
            ssrDarwinGroup: darwinGroup,
          }),
        ),
      },
    }
  } catch (error) {
    {
      let status = 404
      if (isAxiosError(error) && error.status === status) {
        // 404 case
        res.statusCode = status
      } else {
        logger.error(error, 'Error fetching data in collections getServerSideProps')
        Bugsnag.notify(error as NotifiableError, (event) => {
          event.addMetadata('Collection getServerSideProps', {
            resolvedUrl,
            slugPath,
            query: qsSNext,
          })
        })
        status = 500
        res.statusCode = status
      }

      return {
        props: {
          ...(await getServerPageProps(context)),
          ...(await serverSideConfigs(res, locale)),
          error: { status },
          errorStatus: status,
        },
      }
    }
  }
}
export default CollectionPage
